import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import StudioPricingSection from '../components/StudioPricingSection';
import { Sparkles, Video, FileText, Lightbulb, Calendar, TrendingUp, BookOpen, Wand2 } from 'lucide-react';

const TOOLS = [
  { Icon: Video, title: 'Video Studio', desc: 'Short-form scripts with hooks, b-roll cues and a viral score.' },
  { Icon: FileText, title: 'Post Studio', desc: 'Posts, carousels and threads tuned to your brand voice.' },
  { Icon: Lightbulb, title: 'Hook Engine & Idea Lab', desc: 'Generate and score scroll-stopping hooks and ideas.' },
  { Icon: Calendar, title: 'Content Planner', desc: 'Done-for-you content calendars up to 90 days.' },
  { Icon: TrendingUp, title: 'Trends & Creator Analysis', desc: 'Spot what is working and model it for your niche.' },
  { Icon: BookOpen, title: 'Brand Profiles & Library', desc: 'Teach the AI your voice, offers and reference material.' },
];

export default function StudioLanding() {
  return (
    <Layout
      title="Social Media Viral Studio: AI Content for Your Business | DCA Enterprises"
      description="Generate posts, video scripts, hooks, and done-for-you content plans tuned to your brand. Starter $10/mo, Pro $15/mo. Start creating in minutes."
    >
      {/* Hero */}
      <section className="relative bg-[#0A0F1C] overflow-hidden" data-testid="studio-landing-hero">
        <div className="container-custom py-16 lg:py-20 text-center max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.16em] uppercase text-[#A78BFA] mb-4">
            <Sparkles size={14} /> AI Content Engine
          </span>
          <h1 className="heading-1 text-4xl sm:text-5xl lg:text-6xl text-white mb-5">
            Stop staring at a <span className="text-[#A78BFA]">blank screen</span>
          </h1>
          <p className="text-[#CBD5E1] text-lg leading-relaxed mb-8">
            The Social Media Viral Studio turns your brand into ready-to-post content: viral video scripts,
            posts, carousels, hooks and full content calendars, all in your voice.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#studio-pricing-section" className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white px-8 py-4 font-bold rounded-lg transition-colors" data-testid="studio-landing-see-plans">
              See plans
            </a>
            <Link to="/contact" className="bg-white/10 border border-white/30 text-white px-8 py-4 font-bold rounded-lg hover:bg-white/20 transition-colors">
              Talk to David
            </Link>
          </div>
        </div>
      </section>

      {/* What's inside */}
      <section className="section-padding bg-white" data-testid="studio-landing-tools">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="overline mb-3 inline-block">Everything in one studio</span>
            <h2 className="heading-2 text-3xl lg:text-4xl text-[#0A0F1C]">One brand voice, every format</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {TOOLS.map(({ Icon, title, desc }) => (
              <div key={title} className="rounded-2xl border border-[#E2E8F0] p-6 hover:border-[#7C3AED] transition-colors" data-testid={`studio-tool-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <span className="w-11 h-11 rounded-xl bg-[#7C3AED]/10 text-[#7C3AED] flex items-center justify-center mb-3"><Icon size={20} /></span>
                <h3 className="font-bold text-lg text-[#0A0F1C] mb-1">{title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <StudioPricingSection />

      {/* How it works */}
      <section className="section-padding bg-[#F8FAFC]" data-testid="studio-landing-how">
        <div className="container-custom max-w-3xl">
          <div className="text-center mb-10">
            <span className="overline mb-3 inline-block">How it works</span>
            <h2 className="heading-2 text-3xl text-[#0A0F1C]">From sign-up to first post in minutes</h2>
          </div>
          <ol className="space-y-4">
            {[
              ['Create your account', 'Sign up or log in. It takes a few seconds.'],
              ['Choose Starter or Pro', 'Pick the plan that fits and check out securely.'],
              ['Set up your brand', 'Tell the AI your niche, voice and offers once.'],
              ['Generate & post', 'Spin up scripts, posts and content plans on demand.'],
            ].map(([t, d], i) => (
              <li key={t} className="flex items-start gap-4">
                <span className="w-8 h-8 rounded-full bg-[#7C3AED] text-white font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                <div><p className="font-bold text-[#0A0F1C]">{t}</p><p className="text-sm text-[#475569]">{d}</p></div>
              </li>
            ))}
          </ol>
          <div className="text-center mt-10">
            <a href="#studio-pricing-section" className="inline-flex items-center gap-2 bg-[#7C3AED] hover:bg-[#6D28D9] text-white px-8 py-4 font-bold rounded-lg transition-colors">
              <Wand2 size={18} /> Get started now
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
